import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export interface FollowUpListItem {
  id: number;
  studentId: number;
  patientName: string;
  schoolId: string;
  group: string;
  organization: string;
  notes: string;
  followupDate: string;
  completed: boolean;
  completedBy?: string;
  completedDate?: string;
}

export interface FollowUpPageResponse {
  content: FollowUpListItem[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface FollowUpCompletionData {
  completionDate: string;
  emailedPatient: boolean;
  spokeOnPhone: boolean;
  metInPerson: boolean;
  referralCounselingCenter: boolean;
  referralTeamPhysician: boolean;
  referralNutritionist: boolean;
  referralPrimaryCarePhysician: boolean;
  referralExternalMentalHealthProvider: boolean;
  otherResources: string[];
  patientAcceptedReferral: boolean;
  patientDeclinedReferral: boolean;
  completionNotes: string;
}

export interface FollowUpStats {
  total: number;
  completed: number;
  pending: number;
}

export const followUpService = {
  // Get paginated follow-up list
  getFollowUps: async (
    page: number = 0,
    size: number = 10,
    search?: string,
    showCompleted: boolean = false
  ): Promise<FollowUpPageResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      showCompleted: showCompleted.toString()
    });
    
    if (search) {
      params.append('search', search);
    }

    const response = await axios.get(`${API_BASE_URL}/follow-ups?${params}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Get follow-up details
  getFollowUpDetail: async (id: number) => {
    const response = await axios.get(`${API_BASE_URL}/follow-ups/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Complete a follow-up
  completeFollowUp: async (id: number, data: FollowUpCompletionData) => {
    const response = await axios.post(`${API_BASE_URL}/follow-ups/${id}/complete`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Remove a follow-up
  removeFollowUp: async (id: number) => {
    const response = await axios.delete(`${API_BASE_URL}/follow-ups/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Get follow-up history for a student
  getFollowUpHistory: async (studentId: number) => {
    const response = await axios.get(`${API_BASE_URL}/follow-ups/student/${studentId}/history`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Calculate stats from follow-up list
  calculateStats: (followUps: FollowUpListItem[]): FollowUpStats => {
    const total = followUps.length;
    const completed = followUps.filter(f => f.completed).length;
    const pending = total - completed;

    return { total, completed, pending };
  }
};